import React, { useEffect, useMemo, useRef, useState } from "react";
import { TvView } from "./useTvView";
import { createLogLink, getGroupTv } from "api/PriceMonitoring";
import { GroupTvProduct } from "PriceMonitoring/modal";
import { Row, Tooltip } from "antd";

import {
  ColRightAlign,
  GroupTitle,
  HeadrCol,
  MainCol,
  Wrap,
  Table,
  Handle,
  SortableCol,
} from "./styled";
import wb from "./wb.png";
import ozon from "./ozon.png";
import {
  CaretUpOutlined,
  CaretDownOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import GroupItem from "./GroupItem";

type SortKey = "price" | "unit_price" | `query_${number}`;
type SortDir = "asc" | "desc";

const nullLast = (v: number | null, dir: SortDir) =>
  v == null ? (dir === "asc" ? Infinity : -Infinity) : v;

const Group = (props: {
  data: TvView["groups"][number];
  timestamp: string;
  categoryId: number;
}) => {
  const blockDrag = useRef(false);
  const [products, setProducts] = useState<GroupTvProduct[]>([]);
  const [queries, setQueries] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("unit_price");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    return [...products].sort((a, b) => {
      let va: number | null;
      let vb: number | null;
      if (sortKey === "price") {
        va = a.price;
        vb = b.price;
      } else if (sortKey === "unit_price") {
        va = a.unit_price;
        vb = b.unit_price;
      } else {
        const qi = +sortKey.split("_")[1];
        va = a.query_places[qi];
        vb = b.query_places[qi];
      }
      return nullLast(va, sortDir) - nullLast(vb, sortDir);
    });
  }, [products, sortKey, sortDir]);

  const loadData = async () => {
    const result = await getGroupTv(props.data.id);
    setProducts(result.products);
    setQueries(result.queries);
  };

  useEffect(() => {
    loadData();
  }, [props.data.id, props.timestamp]);

  const SortIcon = ({ k }: { k: SortKey }) =>
    sortKey === k ? (
      sortDir === "asc" ? (
        <CaretUpOutlined />
      ) : (
        <CaretDownOutlined />
      )
    ) : null;

  return (
    <Wrap
      draggable
      data-group-id={props.data.id}
      data-category-id={props.categoryId}
      onDragStart={(e) => {
        blockDrag.current && e.preventDefault();
      }}
      onMouseDown={(e) =>
        (blockDrag.current = !(e.target as HTMLElement).closest(".drag_target"))
      }
    >
      <Handle className={"drag_target"} />
      <GroupTitle>{props.data.name}</GroupTitle>
      <Row gutter={[6, 12]}>
        <HeadrCol span={10} style={{ paddingLeft: 20 }}>
          <HistoryOutlined
            onClick={() => {
              createLogLink(props.data.id);
            }}
          />
        </HeadrCol>
        <MainCol span={2}></MainCol>
        <SortableCol span={4} onClick={() => toggleSort("price")}>
          ₽ <SortIcon k="price" />
        </SortableCol>
        <SortableCol span={4} onClick={() => toggleSort("unit_price")}>
          ₽/шт <SortIcon k="unit_price" />
        </SortableCol>
        {queries.map((q, i) => (
          <SortableCol
            span={Math.max(1, Math.floor(4 / queries.length))}
            key={i}
            onClick={() => toggleSort(`query_${i}`)}
          >
            <Tooltip title={q}>Q{i + 1}</Tooltip> <SortIcon k={`query_${i}`} />
          </SortableCol>
        ))}
      </Row>
      <Table>
        {sorted.map((el) => (
          <GroupItem key={el.id + el.link} data={el} queries={queries} />
        ))}
      </Table>
    </Wrap>
  );
};

export default Group;
